/**
 * Redux store — wires up RTK Query + all feature slices.
 *
 * Replaces the B1 stub with the full configured store:
 *   - RTK Query `api` middleware + reducer (reducerPath: 'api')
 *   - auth slice
 *   - notifications slice
 *   - ui slice
 *
 * B4 will update `api.ts` (src/services/api.ts) with the full baseQuery
 * (cookie auth, CSRF injection, 401 refresh, 410 interceptor).  This store
 * file itself should not need changes after B4.
 */
import { configureStore } from '@reduxjs/toolkit';

import { api } from '@/services/api';
import authReducer from '@/features/auth/authSlice';
import notificationsReducer from '@/features/notifications/notificationsSlice';
import uiReducer from '@/features/ui/uiSlice';

export const store = configureStore({
  reducer: {
    [api.reducerPath]: api.reducer,
    auth: authReducer,
    notifications: notificationsReducer,
    ui: uiReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(api.middleware),
});

/** The full Redux state tree — use via `useAppSelector`. */
export type RootState = ReturnType<typeof store.getState>;

/** Typed dispatch — use via `useAppDispatch`. */
export type AppDispatch = typeof store.dispatch;
